import { t } from '@lingui/core/macro'
import { theme as dsTheme } from '@skie-io/react'
import { prepareBoxplotData } from 'echarts/extension/dataTool'
import merge from 'lodash/merge'
import { colorScheme } from '~/components/echarts/colors'
import {
  formatGigabytes,
  labelFormatter,
  labelFormatterWithPrecision,
  labelFormatterWithUnit,
  pieOptionLabelFormatter,
  resolveFormatter,
  tooltipFormatter,
  xAxisFormatter,
  yAxisFormatter,
  yAxisFormatterWithUnit,
} from '~/components/echarts/formatters'
import { largeScaleBarChart } from '~/components/echarts/large_scale_bar_chart'
import { largeScaleStackedAreaChart } from '~/components/echarts/large_scale_stacked_area_chart'
import { pieTooltipFormatter } from '~/components/echarts/pie_tooltip'
import { sortPieSlices } from '~/components/echarts/pie_utils'
import {
  stackedTooltipFormatter,
  stackedTooltipFormatterWithCurrencyPrecision,
  stackedTooltipFormatterWithInteger,
  stackedTooltipFormatterWithNumber,
  stackedTooltipFormatterWithUnit,
} from '~/components/echarts/stacked_tooltip'
import { currencyFormatter } from '~/utils/currency'
import { formatDuration } from '~/utils/format_duration'

const datavizSemantic = dsTheme.other.dataviz
const datavizSeries = dsTheme.other.dataviz.series

const defaults = {
  aria: {
    enabled: true,
  },
  tooltip: {
    padding: 5,
    borderWidth: 1,
    textStyle: {
      fontSize: 12,
    },
    appendTo: 'body',
  },
}

// Create explicit color mapping for compliance states to match stacked bar chart with pie chart
const complianceColors = {
  Compliant: '#59A14F',
  'Critical Noncompliant': '#E15759',
  'Security Noncompliant': '#9467BD',
  'Other Noncompliant': '#F28E2B',
}

// Bootstrap/Tabler severity colors — matches severity_badge helper in Ruby
const severityColors = {
  Critical: '#d63939',
  High: '#f76707',
  Medium: '#f59f00',
  Low: '#066fd1',
  Informational: '#9ca3af',
}

const horizontalBarRowColors = {
  Unattached: datavizSemantic.neutral,
}

function currencyTooltipFormatter(currency = 'USD') {
  const valueFormatter = currencyFormatter(currency)

  return (params) => {
    if (!params || params.length === 0) {
      return ''
    }

    let result = `${params[0].name}<br/>`
    params.forEach((item) => {
      const value = item.value != null ? item.value : 0
      result += `${item.marker} ${item.seriesName}: ${valueFormatter(value)}<br/>`
    })
    return result
  }
}

function pie({ data, series, formatter = 'none', currency = 'USD' }) {
  const tooltipFormatter = pieTooltipFormatter(formatter, currency)

  let pieData = data
  if (series?.[0]?.data) {
    pieData = series[0].data
  }

  // Check if this is compliance data and apply specific colors
  const isComplianceData = pieData.some((item) => complianceColors[item.name])
  const pieColors = isComplianceData
    ? pieData.map((item) => complianceColors[item.name] || '#BAB0AC')
    : colorScheme(pieData)

  return merge(
    {
      color: pieColors,
      tooltip: {
        trigger: 'item',
        formatter: tooltipFormatter,
      },
      series: [
        {
          radius: ['45%', '70%'],
          type: 'pie',
          data: pieData,
        },
      ],
    },
    defaults,
  )
}

function sankey(data) {
  const { points, links } = data.data

  const uniquePoints = []
  const seenNames = new Set()

  points.forEach((point) => {
    if (!seenNames.has(point.name)) {
      uniquePoints.push(point)
      seenNames.add(point.name)
    }
  })

  const pointNames = new Set(uniquePoints.map((p) => p.name))
  const validLinks = links.filter((link) => {
    const sourceExists = pointNames.has(link.source)
    const targetExists = pointNames.has(link.target)

    return sourceExists && targetExists
  })

  return merge(
    {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      series: [
        {
          type: 'sankey',
          layout: 'none',
          data: uniquePoints,
          links: validLinks,
          emphasis: {
            focus: 'adjacency',
          },
          lineStyle: {
            color: 'gradient',
            curveness: 0.1,
            opacity: 0.8,
          },
          itemStyle: {
            borderWidth: 0.5,
            borderColor: '#666',
          },
          label: {
            fontSize: 10,
            overflow: 'truncate',
            width: 80,
            position: 'right',
          },
          nodeWidth: 4,
          nodeGap: 8,
          left: '5%',
          right: '5%',
          top: '15%',
          bottom: '15%',
          orient: 'horizontal',
        },
      ],
    },
    defaults,
  )
}

function boxplot(data) {
  const xAxisValues = Object.entries(data.data).map(([name, _values]) => {
    return name
  })

  const boxRawData = Object.entries(data.data).map(([_name, values]) => {
    return values
  })
  const boxPlotData = prepareBoxplotData(boxRawData)
  const roundedBoxPlotData = boxPlotData.boxData.map((values) => {
    return values.map((ele) => (Number.isInteger(ele) ? ele : ele.toFixed(2)))
  })

  const option = {
    aria: {
      enabled: true,
    },
    tooltip: {
      trigger: 'axis',
      formatter: (param) => {
        const title = param[0].axisValueLabel
        const tooltipData = param[0].data
        const withUnit = (value) => (data.unit ? `${value} ${data.unit}` : value)
        return [
          t`Name: ${title}`,
          t`Upper: ${withUnit(tooltipData[5])}`,
          t`Q3: ${withUnit(tooltipData[4])}`,
          t`Median: ${withUnit(tooltipData[3])}`,
          t`Q1: ${withUnit(tooltipData[2])}`,
          t`Lower: ${withUnit(tooltipData[1])}`,
        ].join('<br/>')
      },
      axisPointer: {
        type: 'shadow',
      },
      appendTo: 'body',
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
    },
    xAxis: {
      type: 'category',
      data: xAxisValues,
      boundaryGap: true,
      nameGap: 30,
      splitArea: {
        show: false,
      },
      axisLabel: {
        formatter: '{value}',
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      splitArea: {
        show: true,
      },
    },
    series: [
      {
        name: t`Boxplot`,
        type: 'boxplot',
        data: roundedBoxPlotData,
      },
      {
        name: t`Outlier`,
        type: 'scatter',
        data: data.outliers,
      },
    ],
  }
  return option
}

function pieWithQuantityAndTotal({ data, total, formatter = 'none', currency = 'USD' }) {
  const sortedData = sortPieSlices(data)
  const totalFormatter = resolveFormatter(formatter, currency)
  const tooltipFormatter = pieTooltipFormatter(formatter, currency)

  return merge(
    {
      color: colorScheme(sortedData),
      tooltip: {
        trigger: 'item',
        formatter: tooltipFormatter,
      },
      series: [
        {
          radius: ['45%', '70%'],
          type: 'pie',
          data: sortedData,
        },
      ],
      graphic: [
        {
          left: 'center',
          top: 'middle',
          type: 'text',
          silent: true,
          style: {
            text: totalFormatter(total),
            font: 'bold 16px sans-serif',
            fill: '#333',
          },
        },
      ],
    },
    defaults,
  )
}

function pieWithTotal({ data, total, formatter = 'none', currency = 'USD' }, echartsTheme) {
  const pieOptions = pie({
    data,
    formatter: pieOptionLabelFormatter(formatter, currency),
    currency,
  })
  const totalFormatter = resolveFormatter(formatter, currency)

  return merge(
    {
      // renders the total amount in the middle
      graphic: [
        {
          left: 'center',
          top: 'middle',
          type: 'text',
          silent: true,
          style: {
            text: totalFormatter(total),
            font: 'bold 16px sans-serif',
            fill: echartsTheme === 'dark' ? '#E5E7EB' : '#333',
          },
        },
      ],
    },
    pieOptions,
  )
}

function stackedBarWithTotal(rawData, options = {}) {
  const { data, total, x, currency, formatter } = rawData
  const totalSeriesName = options.totalSeriesName ? options.totalSeriesName : t`Total`
  const totalSeriesData = options.totalSeriesData ? options.totalSeriesData : total
  const totalSeriesFormatter =
    'totalSeriesFormatter' in options ? options.totalSeriesFormatter : labelFormatter(currency, formatter)
  const xAxisTooltipFormatter =
    'axisTooltipFormatter' in options ? options.axisTooltipFormatter : stackedTooltipFormatter(currency, formatter)
  const lineStyleAttrs = options.lineStyleAttrs ? options.lineStyleAttrs : {}
  const yAxisLabelFormatter =
    'yAxisLabelFormatter' in options ? options.yAxisLabelFormatter : yAxisFormatter(currency, formatter)

  const regularSeries = Object.entries(data).map(([name, values]) => {
    const series = {
      name,
      stack: 'x',
      type: 'bar',
      emphasis: {
        focus: 'series',
      },
      data: values,
    }

    // Apply specific color if this is compliance or severity data
    if (complianceColors[name]) series.color = complianceColors[name]
    if (severityColors[name]) series.color = severityColors[name]

    return series
  })

  // When severity series are present, use a neutral trend line so it doesn't clash with High (orange)
  const hasSeverityData = Object.keys(data).some((name) => severityColors[name])
  const effectiveLineStyle = hasSeverityData ? { ...lineStyleAttrs, color: '#374151' } : lineStyleAttrs

  const totalSeries = {
    name: totalSeriesName,
    type: 'line',
    data: totalSeriesData,
    label: {
      show: true,
      formatter: totalSeriesFormatter,
      // fixed regardless of theme: this label always sits on its own light backgroundColor, so its
      // text must stay dark even in the dark theme, where labels otherwise default to a light color
      color: '#1a1a1a',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      padding: 3,
    },
    lineStyle: effectiveLineStyle,
    labelLayout: {
      hideOverlap: true,
    },
  }

  const series = regularSeries.concat(totalSeries)

  const { values: xAxisValues, format: xAxisFormat } = x

  return merge(
    {
      color: colorScheme(series),
      grid: {
        top: 50,
        bottom: 0,
        left: 20,
        right: 20,
        containLabel: true,
      },
      tooltip: {
        extraCssText: 'overflow-y: auto; max-height: 95%;',
        trigger: 'axis',
        formatter: xAxisTooltipFormatter,
        axisPointer: {
          type: 'shadow',
          label: {
            formatter: tooltipFormatter(xAxisFormat),
          },
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisValues,
        axisLabel: {
          rotate: 45,
          formatter: xAxisFormatter(xAxisFormat),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: yAxisLabelFormatter,
        },
      },
      series,
    },
    defaults,
  )
}

function groupedBarChart({ data, x, currency, formatter }) {
  const yAxisLabelFormatter = yAxisFormatter(currency, formatter)
  const xAxisTooltipFormatter = stackedTooltipFormatter(currency, formatter)

  const series = Object.entries(data).map(([name, values]) => ({
    name,
    type: 'bar',
    emphasis: {
      focus: 'series',
    },
    data: values,
  }))

  const { values: xAxisValues, format: xAxisFormat } = x

  return merge(
    {
      color: colorScheme(series),
      legend: { show: true, top: 0 },
      grid: {
        top: 40,
        bottom: 0,
        left: 20,
        right: 20,
        containLabel: true,
      },
      tooltip: {
        extraCssText: 'overflow-y: auto; max-height: 95%;',
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: xAxisTooltipFormatter,
      },
      xAxis: {
        type: 'category',
        data: xAxisValues,
        axisLabel: {
          rotate: 45,
          formatter: xAxisFormatter(xAxisFormat),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: yAxisLabelFormatter,
        },
      },
      series,
    },
    defaults,
  )
}

function optimizationSummaryChart(data, currency = 'USD') {
  const { legend, grid, xAxis, yAxis, series } = data
  const formatter = currencyTooltipFormatter(currency)

  return merge(
    {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter,
      },
      legend,
      grid,
      xAxis,
      yAxis,
      series,
    },
    defaults,
  )
}

function pieChart(data) {
  const { legend, series } = data

  return merge(
    {
      tooltip: {
        trigger: 'item',
        formatter: pieTooltipFormatter('none', 'USD'),
      },
      legend,
      series,
    },
    defaults,
  )
}

function optimizationCostAnalysisChart(data, currency = 'USD') {
  const { legend, grid, xAxis, yAxis, series } = data
  const tooltip = {
    ...(data.tooltip || {}),
    formatter: currencyTooltipFormatter(currency),
  }
  const axisLabelFormatter = currencyFormatter(currency, 'compact')
  const formattedXAxis = {
    ...xAxis,
    axisLabel: {
      ...(xAxis?.axisLabel || {}),
      formatter: axisLabelFormatter,
    },
  }

  return merge(
    {
      tooltip,
      legend,
      grid,
      xAxis: formattedXAxis,
      yAxis,
      series,
    },
    defaults,
  )
}

function azureOptimizationSummary(data, currency = 'USD') {
  const { data: chartData, metadata } = data
  const { categories, series } = chartData
  const { xAxisLabel } = metadata
  const formatter = currencyTooltipFormatter(currency)

  return merge(
    {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter,
      },
      legend: {
        data: series.map((item) => item.name),
        top: '5%',
      },
      grid: {
        top: '25%',
        bottom: '15%',
        left: '3%',
        right: '4%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
      },
      yAxis: {
        type: 'category',
        data: categories,
      },
      series: series.map((item) => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        data: item.data,
        itemStyle: { color: item.color },
      })),
    },
    defaults,
  )
}

function areaChartWithTotal({ data, x, currency = 'USD' }) {
  const valueFormatter = currencyFormatter(currency)
  const { values: xAxisValues, format: xAxisFormat } = x
  const axisValueFormatter = xAxisFormatter(xAxisFormat)

  return merge(
    {
      color: [datavizSeries[0]],
      grid: { top: 24, bottom: 12, left: 10, right: 80, containLabel: true },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const [point] = params
          return `${axisValueFormatter(point.axisValue)}<br/>${point.marker} ${valueFormatter(point.value)}`
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisValues,
        axisLabel: { rotate: 45, formatter: axisValueFormatter },
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'line',
          data,
          smooth: false,
          showSymbol: false,
          areaStyle: { opacity: 0.25 },
          lineStyle: { width: 3 },
          endLabel: {
            show: true,
            formatter: (params) => valueFormatter(params.value),
            fontWeight: 'bold',
            fontSize: 13,
          },
        },
      ],
    },
    defaults,
  )
}

function barChartWithAverage({ data, x, average, currency = 'USD' }) {
  const { values: xAxisValues, format: xAxisFormat } = x
  const axisValueFormatter = xAxisFormatter(xAxisFormat)
  const valueFormatter = currencyFormatter(currency)

  return merge(
    {
      color: [datavizSeries[2]],
      grid: { top: 24, bottom: 12, left: 10, right: 80, containLabel: true },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const [point] = params
          return `${axisValueFormatter(point.axisValue)}<br/>${point.marker} ${valueFormatter(point.value)}`
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisValues,
        axisLabel: { rotate: 45, formatter: axisValueFormatter },
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'bar',
          data,
          barMaxWidth: 26,
          markLine: {
            silent: true,
            symbol: 'none',
            data: [{ yAxis: average }],
            lineStyle: { type: 'dashed', color: datavizSemantic.neutral },
            label: { formatter: t`avg`, position: 'end', fontSize: 12 },
          },
        },
      ],
    },
    defaults,
  )
}

function stackedBarWithTable({ data }) {
  const palette = colorScheme(data)

  return merge(
    {
      grid: { top: 2, bottom: 2, left: 2, right: 2 },
      tooltip: {
        trigger: 'item',
        formatter: (params) => `${params.marker} ${params.seriesName}: ${formatGigabytes(params.value)}`,
      },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', data: [''], show: false },
      series: data.map((entry, index) => ({
        name: entry.name,
        type: 'bar',
        stack: 'total',
        barWidth: '100%',
        data: [entry.space],
        itemStyle: { color: palette[index % palette.length] },
      })),
    },
    defaults,
  )
}

function horizontalBarRows({ data, total }) {
  const sorted = [...data].sort((a, b) => b.value - a.value)
  const palette = colorScheme(sorted)

  return merge(
    {
      grid: { top: 12, bottom: 12, left: 90, right: 36, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const [point] = params
          const share = total ? ` (${((point.value / total) * 100).toFixed(1)}%)` : ''
          return `${point.marker} ${point.name}: ${point.value}${share}`
        },
      },
      xAxis: { type: 'value', show: false },
      yAxis: {
        type: 'category',
        data: sorted.map((d) => d.name),
        inverse: true,
        axisLabel: { fontSize: 13 },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 24,
          label: { show: true, position: 'right', fontWeight: 700, fontSize: 13 },
          data: sorted.map((d, i) => ({
            value: d.value,
            name: d.name,
            itemStyle: {
              color: horizontalBarRowColors[d.name] ?? palette[i % palette.length],
            },
          })),
        },
      ],
    },
    defaults,
  )
}

export function optionsForChart(data, echartsTheme) {
  const { type, chartType, currency = 'USD' } = data
  const chartTypeToUse = chartType || type

  switch (chartTypeToUse) {
    case 'pie':
      return pie(data)
    case 'pie_with_total':
      return pieWithTotal(data, echartsTheme)
    case 'horizontal_bar_rows':
      return horizontalBarRows(data)
    case 'stacked_bar_with_total':
      return stackedBarWithTotal(data)
    case 'grouped_bar':
      return groupedBarChart(data)
    case 'stacked_bar_with_trend_line': {
      const tooltipFormatter =
        data.number_format === 'integer' ? stackedTooltipFormatterWithInteger : stackedTooltipFormatterWithNumber
      const options = {
        lineStyleAttrs: {
          color: 'orange',
          width: 3,
          type: 'dashed',
        },
        totalSeriesData: data.trend_line,
        axisTooltipFormatter: tooltipFormatter,
        totalSeriesName: 'Trend Line',
        totalSeriesFormatter: null,
        yAxisLabelFormatter: null,
      }
      return stackedBarWithTotal(data, options)
    }
    case 'stacked_bar_with_line': {
      const options = {
        lineStyleAttrs: {
          color: 'orange',
          width: 3,
          type: 'dashed',
        },
        totalSeriesData: data.line,
        axisTooltipFormatter: null,
        totalSeriesName: data.line_series_name,
        totalSeriesFormatter: null,
        yAxisLabelFormatter: null,
      }
      return stackedBarWithTotal(data, options)
    }
    case 'stacked_bar_with_high_precision': {
      if (data.unit) {
        // Use unit formatting instead of currency
        const options = {
          axisTooltipFormatter: stackedTooltipFormatterWithUnit(data.unit),
          totalSeriesFormatter: labelFormatterWithUnit(data.unit),
          yAxisLabelFormatter: yAxisFormatterWithUnit(data.unit),
        }
        return stackedBarWithTotal(data, options)
      } else {
        // Default to currency formatting
        const options = {
          axisTooltipFormatter: stackedTooltipFormatterWithCurrencyPrecision(currency),
          totalSeriesFormatter: labelFormatterWithPrecision(currency),
        }
        return stackedBarWithTotal(data, options)
      }
    }
    case 'pie_with_quantity_and_total':
      return pieWithQuantityAndTotal(data)
    case 'boxplot':
      return boxplot(data)
    case 'sankey':
      return sankey(data)
    case 'large_scale_bar_chart':
      return largeScaleBarChart(data)
    case 'large_scale_stacked_area_chart_with_limit_line':
      return largeScaleStackedAreaChart(data)
    case 'azure_optimization_summary':
      return azureOptimizationSummary(data, currency)
    case 'aws_optimization_summary':
    case 'ec2_optimization_summary':
    case 'ec2_savings_plans_management':
    case 'rds_licensed_ri_chart':
    case 'rds_open_source_ri_chart':
      return optimizationSummaryChart(data, currency)
    case 'rds_instance_class_charts':
    case 'ebs_volume_kind_charts':
    case 'ec2_instance_type_charts':
      return pieChart(data)
    case 'rds_optimization_cost_analysis_chart':
    case 'optimization_storage_cost_analysis_chart':
    case 'azure_vm_cost_analysis_chart':
    case 'azure_database_cost_analysis_chart':
    case 'azure_disk_cost_analysis_chart':
    case 'elasti_cache_optimization_cost_analysis_chart':
    case 'opensearch_optimization_cost_analysis_chart':
      return optimizationCostAnalysisChart(data, currency)
    case 'heatmap':
      return peerHeatmap(data)
    case 'area_chart_with_total':
      return areaChartWithTotal(data)
    case 'bar_chart_with_average':
      return barChartWithAverage(data)
    case 'stacked_bar_with_table':
      return stackedBarWithTable(data)
    case 'duration_line_chart':
      return {
        ...data,
        tooltip: {
          ...data.tooltip,
          formatter: (params) => {
            if (!params?.length) return ''
            const lines = params
              .filter((p) => p.value != null)
              .map((p) => `${p.marker} ${p.seriesName}: ${formatDuration(p.value)}`)
            return `${params[0].name}<br/>${lines.join('<br/>')}`
          },
        },
        yAxis: {
          ...data.yAxis,
          axisLabel: {
            ...data.yAxis?.axisLabel,
            formatter: (v) => formatDuration(v),
          },
        },
      }
    default:
      // If no type is specified, assume it's already in ECharts format
      return data
  }
}

function peerHeatmap({ x_axis: xAxis, y_axis: yAxis, data, thresholds = [1, 2, 3, 4] }) {
  const [t1, t2, t3, t4] = thresholds
  const pieces = [
    { min: -Infinity, max: t1, color: '#f3f4f6', label: `≤ ${t1}σ` },
    { min: t1, max: t2, color: '#fef3c7', label: `${t1}–${t2}σ` },
    { min: t2, max: t3, color: '#fed7aa', label: `${t2}–${t3}σ` },
    { min: t3, max: t4, color: '#fecaca', label: `${t3}–${t4}σ` },
    { min: t4, max: Infinity, color: '#ef4444', label: `> ${t4}σ` },
  ]

  return {
    tooltip: {
      position: 'top',
      formatter: (p) => {
        const [xIndex, yIndex, sigma] = p.data
        const yData = xAxis[xIndex]
        const xData = yAxis[yIndex]
        let severity
        if (sigma < t1) {
          severity = 'Within normal range'
        } else if (sigma < t2) {
          severity = `Watch (${t1}–${t2}σ)`
        } else if (sigma < t3) {
          severity = `Warn (${t2}–${t3}σ)`
        } else if (sigma < t4) {
          severity = `Alert (${t3}–${t4}σ)`
        } else {
          severity = `Critical (>${t4}σ)`
        }
        return `<strong>${xData}</strong><br/>${yData}<br/>${sigma.toFixed(1)}σ <br/><span style="color:#6b7280">${severity}</span>`
        // return `<span style="color:#6b7280">${severity}</span>`
      },
    },
    grid: { left: 160, right: 60, top: 30, bottom: 140, containLabel: false },
    xAxis: {
      type: 'category',
      data: xAxis,
      axisLabel: { rotate: 40, interval: 0, fontSize: 11, align: 'right', margin: 14 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: yAxis,
      inverse: true,
      axisLabel: { fontSize: 12, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    visualMap: {
      show: true,
      type: 'piecewise',
      pieces,
      orient: 'horizontal',
      right: 60,
      top: 0,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 11, color: '#6b7280' },
    },
    series: [
      {
        type: 'heatmap',
        data,
        label: {
          show: true,
          fontSize: 11,
          fontWeight: 500,
          formatter: (p) => (p.data[2] >= t1 ? `${p.data[2].toFixed(1)}σ` : '—'),
          color: (p) => (p.data[2] >= t4 ? '#ffffff' : p.data[2] >= t1 ? '#1f2937' : '#9ca3af'),
        },
        itemStyle: { borderWidth: 2, borderColor: '#ffffff' },
        emphasis: {
          itemStyle: {
            borderColor: '#3b82f6',
            borderWidth: 2,
            shadowBlur: 4,
            shadowColor: 'rgba(59,130,246,0.3)',
          },
        },
      },
    ],
  }
}
